import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { ProtocolTypeService } from '@artemis-software/wr-api';
import { environment } from '../../../environments/environment';
import { SensorCleaningService } from '@artemis-software/wr-api';

interface ImportResult {
  protocolTypeId: string;
  protocolTypeName: string;
  createdFieldCount: number;
  reusedFieldCount: number;
  createdCollectionCount: number;
  reusedCollectionCount: number;
}

@Component({
  standalone: false,
  selector: 'wr-debug-page',
  templateUrl: './debug-page.component.html',
  styleUrls: ['./debug-page.component.scss'],
})
export class DebugPageComponent {

  readonly sensorCleaningService = inject(SensorCleaningService);
  currentEnvironment = environment;

  selectedFile: File | null = null;
  importing = false;
  importResult: ImportResult | null = null;
  importError: string | null = null;

  constructor(
    private readonly protocolTypeService: ProtocolTypeService,
    private readonly router: Router,
    private readonly toastrService: NbToastrService,
  ) {}

  throwTestError(): void {
    throw new Error('Sentry Test Error');
  }

  async startSensorCleaning() {
    const result = await firstValueFrom(this.sensorCleaningService.cleanSensors()).catch(() => undefined);
    console.log(result);
  }

  async downloadGuideline(): Promise<void> {
    try {
      const guideline = await firstValueFrom(this.protocolTypeService.getClaudeGuideline());
      const json = JSON.stringify(guideline, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'claude-protocol-guideline.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      this.toastrService.danger('Guideline konnte nicht geladen werden.', 'Fehler');
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    this.importResult = null;
    this.importError = null;
  }

  async importFromClaude(): Promise<void> {
    if (!this.selectedFile) return;
    this.importing = true;
    this.importResult = null;
    this.importError = null;
    try {
      const text = await this.selectedFile.text();
      const parsed = JSON.parse(text);
      const result: ImportResult = await firstValueFrom(this.protocolTypeService.importFromClaude(parsed));
      this.importResult = result;
      this.toastrService.success(`"${result.protocolTypeName}" wurde erstellt.`, 'Import erfolgreich');
    } catch (err: any) {
      const message = err?.error?.message ?? err?.message ?? 'Unbekannter Fehler';
      this.importError = message;
      this.toastrService.danger(message, 'Import fehlgeschlagen');
    } finally {
      this.importing = false;
    }
  }

  openProtocolType(id: string): void {
    this.router.navigate(['/protocolTypes', id]);
  }
}
